/**
 * Phase 16 (F7/CO2) — Quick Picker page.
 *
 * Two-tab grid view that lets the user fly through menu + theme picks
 * across every day in one screen. Rows = restaurants (tab 1) or buffet
 * venues (tab 2). Cols = days. Each cell is a small dropdown that
 * persists immediately via `patchDay` (debounced) so the Meal Plan
 * Editor reflects the change on next open.
 *
 * Backend contract: no new endpoints. Reads `voyages/:id` for the full
 * day list + library reads; writes via `patchDay`.
 */
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import {
  ChevronLeft, CalendarDays, Loader2, Soup, Pizza,
} from "lucide-react";
import { getVoyage } from "@/lib/voyagesApi";
import {
  getDay, patchDay, listRestaurants, listThemeBuffets,
} from "@/api/mealPlanApi";

const NONE = "__none__";

// Coerce a `MenuEntry[]` or legacy `List[str]` → `[{id?, name}]`.
function menuPairsOf(menus) {
  if (!Array.isArray(menus)) return [];
  return menus.map((m) => {
    if (typeof m === "string") return { id: null, name: m };
    if (m && typeof m === "object") return { id: m.id || null, name: m.name || "" };
    return null;
  }).filter((m) => m && m.name);
}

export default function QuickPicker() {
  const { id: voyageId } = useParams();
  const [voyage, setVoyage] = useState(null);
  const [days, setDays] = useState([]); // [{day_number, meal_plan}, ...]
  const [restaurants, setRestaurants] = useState([]);
  const [themes, setThemes] = useState([]);
  const [tab, setTab] = useState("menus");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // {`<day>:<slot>:<rid>`: bool}

  const reloadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [v, rs, tb] = await Promise.all([
        getVoyage(voyageId),
        listRestaurants(),
        listThemeBuffets(),
      ]);
      setVoyage(v);
      setRestaurants(rs);
      setThemes(tb);
      // Each day's meal_plan is included in getVoyage's response.
      const ds = (v.days || []).map((d) => ({
        day_number: d.day_number,
        meal_plan: d.meal_plan || {},
      }));
      setDays(ds);
    } catch (e) {
      toast({
        title: "Couldn't load voyage",
        description: String(e?.message || e),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [voyageId]);

  useEffect(() => {
    // T1 — defensive token guard. AccessGate already withholds children
    // until the URL `?token=…` lands in localStorage, but Quick Picker
    // mounted directly via deep-link occasionally raced the gate in
    // testing (browser-use reported a one-shot 401 on initial render).
    // Defer the first `reloadAll` until the apiClient interceptor has
    // a token to attach; re-listen for the gate's success event so we
    // refire the moment the gate completes.
    const fire = () => {
      const tok = (typeof window !== "undefined"
        && window.localStorage
        && window.localStorage.getItem("access_token")) || "";
      if (tok) reloadAll();
    };
    fire();
    const onValidated = () => fire();
    if (typeof window !== "undefined") {
      window.addEventListener("access-token-validated", onValidated);
      return () => window.removeEventListener("access-token-validated", onValidated);
    }
    return undefined;
  }, [reloadAll]);

  // Regular restaurants only (tab 1).
  const regularRests = useMemo(
    () => (restaurants || []).filter((r) => r.type === "regular"),
    [restaurants],
  );

  // ============================================================
  // Cell persistence — debounced patchDay
  // ============================================================
  const debouncers = useRef({});
  const setCellMenu = (dayNumber, restaurantId, slot, menuPair) => {
    const key = `${dayNumber}::${restaurantId}::${slot}`;
    // Optimistic local update so the dropdown reflects immediately.
    setDays((ds) =>
      ds.map((d) => {
        if (d.day_number !== dayNumber) return d;
        const mp = d.meal_plan || {};
        const rrs = Array.isArray(mp.regular_restaurants) ? [...mp.regular_restaurants] : [];
        const ridx = rrs.findIndex((rr) => rr.restaurant_id === restaurantId);
        const base = ridx >= 0 ? rrs[ridx] : {
          restaurant_id: restaurantId,
          restaurant_name:
            (regularRests.find((r) => r.id === restaurantId) || {}).name || "",
        };
        const next = {
          ...base,
          [`${slot}_menu_id`]: menuPair?.id || null,
          [`${slot}_menu_name`]: menuPair?.name || null,
        };
        if (ridx >= 0) rrs[ridx] = next; else rrs.push(next);
        return { ...d, meal_plan: { ...mp, regular_restaurants: rrs } };
      }),
    );
    // Debounce the network call so a fast user clicking through many
    // cells doesn't spam the backend.
    if (debouncers.current[key]) clearTimeout(debouncers.current[key]);
    debouncers.current[key] = setTimeout(async () => {
      setBusy((b) => ({ ...b, [key]: true }));
      try {
        // Re-read the day so we don't clobber other concurrent edits.
        const fresh = await getDay(voyageId, dayNumber);
        const mp = fresh?.meal_plan || {};
        const rrs = Array.isArray(mp.regular_restaurants) ? [...mp.regular_restaurants] : [];
        const ridx = rrs.findIndex((rr) => rr.restaurant_id === restaurantId);
        const base = ridx >= 0 ? rrs[ridx] : {
          restaurant_id: restaurantId,
          restaurant_name:
            (regularRests.find((r) => r.id === restaurantId) || {}).name || "",
        };
        const next = {
          ...base,
          [`${slot}_menu_id`]: menuPair?.id || null,
          [`${slot}_menu_name`]: menuPair?.name || null,
        };
        if (ridx >= 0) rrs[ridx] = next; else rrs.push(next);
        await patchDay(voyageId, dayNumber, {
          meal_plan: { ...mp, regular_restaurants: rrs },
        });
      } catch (e) {
        toast({
          title: "Save failed",
          description: String(e?.message || e),
          variant: "destructive",
        });
        reloadAll();
      } finally {
        setBusy((b) => ({ ...b, [key]: false }));
      }
    }, 450);
  };

  // Buffet venue theme cell change. Buffets are keyed by restaurant_id
  // (the buffet venue lives in lib_restaurants with type="buffet").
  const setBuffetTheme = (dayNumber, buffetRestaurantId, slot, themePair) => {
    const key = `b::${dayNumber}::${buffetRestaurantId}::${slot}`;
    setDays((ds) =>
      ds.map((d) => {
        if (d.day_number !== dayNumber) return d;
        const mp = d.meal_plan || {};
        const buffets = Array.isArray(mp.buffets) ? [...mp.buffets] : [];
        const bidx = buffets.findIndex(
          (b) => b.restaurant_id === buffetRestaurantId,
        );
        const base = bidx >= 0 ? buffets[bidx] : {
          restaurant_id: buffetRestaurantId,
          [slot]: {},
        };
        const meal = base[slot] || {};
        const nextMeal = {
          ...meal,
          theme_buffet_id: themePair?.id || null,
          theme_buffet_name: themePair?.name || "",
        };
        const nextB = { ...base, [slot]: nextMeal };
        if (bidx >= 0) buffets[bidx] = nextB; else buffets.push(nextB);
        return { ...d, meal_plan: { ...mp, buffets } };
      }),
    );
    if (debouncers.current[key]) clearTimeout(debouncers.current[key]);
    debouncers.current[key] = setTimeout(async () => {
      setBusy((b) => ({ ...b, [key]: true }));
      try {
        const fresh = await getDay(voyageId, dayNumber);
        const mp = fresh?.meal_plan || {};
        const buffets = Array.isArray(mp.buffets) ? [...mp.buffets] : [];
        const bidx = buffets.findIndex(
          (b) => b.restaurant_id === buffetRestaurantId,
        );
        const base = bidx >= 0 ? buffets[bidx] : { restaurant_id: buffetRestaurantId };
        const meal = base[slot] || {};
        const nextMeal = {
          ...meal,
          theme_buffet_id: themePair?.id || null,
          theme_buffet_name: themePair?.name || "",
        };
        const nextB = { ...base, [slot]: nextMeal };
        if (bidx >= 0) buffets[bidx] = nextB; else buffets.push(nextB);
        await patchDay(voyageId, dayNumber, {
          meal_plan: { ...mp, buffets },
        });
      } catch (e) {
        toast({
          title: "Save failed",
          description: String(e?.message || e),
          variant: "destructive",
        });
        reloadAll();
      } finally {
        setBusy((b) => ({ ...b, [key]: false }));
      }
    }, 450);
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading Quick Picker…
      </div>
    );
  }
  if (!voyage) {
    return (
      <div className="p-6 text-sm text-muted-foreground">Voyage not found.</div>
    );
  }

  // Buffet venues from lib_restaurants (type="buffet" + "crew_mess_buffet").
  const buffetVenues = (restaurants || []).filter(
    (r) => r.type === "buffet" || r.type === "crew_mess_buffet",
  );

  return (
    <div className="space-y-3 p-4" data-testid="quick-picker-page">
      <div className="flex items-center justify-between border-b pb-3">
        <div className="flex items-center gap-3">
          <Link
            to={`/voyages/${voyageId}`}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            data-testid="quick-picker-back"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Back to Voyage
          </Link>
          <span className="text-muted-foreground">·</span>
          <div className="flex items-center gap-1.5">
            <CalendarDays className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-semibold">Quick Picker</span>
          </div>
        </div>
        <div className="text-xs text-muted-foreground">
          {voyage.voyage_number} · {voyage.ship_name} · {voyage.total_days} days
        </div>
      </div>

      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={() => setTab("menus")}
          className={`rounded border px-3 py-1 text-xs ${
            tab === "menus" ? "border-amber-400 bg-amber-100 text-amber-900" : "bg-white"
          }`}
          data-testid="quick-picker-tab-menus"
        >
          <Pizza className="mr-1 inline h-3 w-3" /> Restaurant Menus
        </button>
        <button
          type="button"
          onClick={() => setTab("buffets")}
          className={`rounded border px-3 py-1 text-xs ${
            tab === "buffets" ? "border-emerald-400 bg-emerald-100 text-emerald-900" : "bg-white"
          }`}
          data-testid="quick-picker-tab-buffets"
        >
          <Soup className="mr-1 inline h-3 w-3" /> Theme Buffets
        </button>
      </div>

      {tab === "menus" && (
        <MenusGrid
          days={days}
          restaurants={regularRests}
          onSet={setCellMenu}
          busy={busy}
        />
      )}
      {tab === "buffets" && (
        <BuffetsGrid
          days={days}
          buffetVenues={buffetVenues}
          themes={themes}
          onSet={setBuffetTheme}
          busy={busy}
        />
      )}
    </div>
  );
}

function MenusGrid({ days, restaurants, onSet, busy }) {
  if ((restaurants || []).length === 0) {
    return <div className="text-xs text-muted-foreground p-4">
      No regular restaurants configured for this voyage's ship.
    </div>;
  }
  return (
    <div className="overflow-x-auto rounded border bg-white">
      <table className="min-w-full text-xs">
        <thead className="bg-slate-50">
          <tr>
            <th className="sticky left-0 z-10 border-r bg-slate-50 px-2 py-2 text-left">
              Restaurant
            </th>
            {days.map((d) => (
              <th key={d.day_number} className="border-r px-2 py-2 text-center">
                Day {d.day_number}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {restaurants.map((r) => (
            <tr key={r.id} className="border-t">
              <td className="sticky left-0 z-10 border-r bg-white px-2 py-1.5 font-medium">
                {r.name}
              </td>
              {days.map((d) => {
                const rr = (d.meal_plan?.regular_restaurants || []).find(
                  (x) => x.restaurant_id === r.id,
                );
                return (
                  <td
                    key={d.day_number}
                    className="border-r p-1 align-top min-w-[140px]"
                    data-testid={`quick-picker-cell-r-${r.id}-d-${d.day_number}`}
                  >
                    <div className="space-y-1">
                      <CellMenuSelect
                        slot="lunch"
                        rid={r.id}
                        day={d.day_number}
                        menus={menuPairsOf(r.lunch_menus)}
                        currentName={rr?.lunch_menu_name || null}
                        onSet={onSet}
                        busy={busy[`${d.day_number}::${r.id}::lunch`]}
                      />
                      <CellMenuSelect
                        slot="dinner"
                        rid={r.id}
                        day={d.day_number}
                        menus={menuPairsOf(r.dinner_menus)}
                        currentName={rr?.dinner_menu_name || null}
                        onSet={onSet}
                        busy={busy[`${d.day_number}::${r.id}::dinner`]}
                      />
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CellMenuSelect({ slot, rid, day, menus, currentName, onSet, busy }) {
  const value = (() => {
    const m = menus.find((x) => x.name === currentName);
    return m ? (m.id || `name::${m.name}`) : NONE;
  })();
  const pick = (val) => {
    if (val === NONE) {
      onSet(day, rid, slot, null);
      return;
    }
    let pair;
    if (val.startsWith("name::")) {
      pair = { id: null, name: val.slice(6) };
    } else {
      pair = menus.find((x) => x.id === val) || null;
    }
    onSet(day, rid, slot, pair);
  };
  return (
    <div className="flex items-center gap-1">
      <span className="w-9 text-[9px] uppercase text-muted-foreground">
        {slot === "lunch" ? "L" : slot === "dinner" ? "D" : slot.slice(0, 2)}
      </span>
      <Select value={value} onValueChange={pick}>
        <SelectTrigger
          className="h-6 flex-1 text-[10px]"
          data-testid={`quick-picker-cell-${slot}-r-${rid}-d-${day}`}
        >
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>— (no menu) —</SelectItem>
          {menus.length === 0 ? (
            <div className="px-2 py-1 text-[9px] italic text-muted-foreground">
              No menus defined
            </div>
          ) : (
            menus.map((m, i) => (
              <SelectItem
                key={m.id || `name::${m.name}-${i}`}
                value={m.id || `name::${m.name}`}
              >
                {m.name}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
      {busy && <Loader2 className="h-3 w-3 animate-spin text-amber-500" />}
    </div>
  );
}

function BuffetsGrid({ days, buffetVenues, themes, onSet, busy }) {
  if ((buffetVenues || []).length === 0) {
    return <div className="text-xs text-muted-foreground p-4">
      No buffet venues configured for this voyage's ship.
    </div>;
  }
  return (
    <div className="overflow-x-auto rounded border bg-white">
      <table className="min-w-full text-xs">
        <thead className="bg-slate-50">
          <tr>
            <th className="sticky left-0 z-10 border-r bg-slate-50 px-2 py-2 text-left">
              Buffet venue
            </th>
            {days.map((d) => (
              <th key={d.day_number} className="border-r px-2 py-2 text-center">
                Day {d.day_number}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {buffetVenues.map((v) => (
            <tr key={v.id} className="border-t">
              <td className="sticky left-0 z-10 border-r bg-white px-2 py-1.5 font-medium">
                {v.name}
                <div className="text-[9px] text-muted-foreground">{v.type}</div>
              </td>
              {days.map((d) => {
                const buffet = (d.meal_plan?.buffets || []).find(
                  (b) => b.restaurant_id === v.id,
                );
                return (
                  <td
                    key={d.day_number}
                    className="border-r p-1 align-top min-w-[140px]"
                    data-testid={`quick-picker-cell-buf-${v.id}-d-${d.day_number}`}
                  >
                    <div className="space-y-1">
                      {["breakfast", "lunch", "dinner"].map((slot) => {
                        const meal = buffet?.[slot];
                        const cur = meal?.theme_buffet_id ||
                                     (meal?.theme_buffet_name ? `name::${meal.theme_buffet_name}` : NONE);
                        return (
                          <div key={slot} className="flex items-center gap-1">
                            <span className="w-9 text-[9px] uppercase text-muted-foreground">
                              {slot.slice(0, 1).toUpperCase()}
                            </span>
                            <Select
                              value={cur}
                              onValueChange={(val) => {
                                if (val === NONE) { onSet(d.day_number, v.id, slot, null); return; }
                                if (val.startsWith("name::")) {
                                  onSet(d.day_number, v.id, slot, { id: null, name: val.slice(6) });
                                  return;
                                }
                                const t = (themes || []).find((x) => x.id === val);
                                onSet(d.day_number, v.id, slot, t ? { id: t.id, name: t.name } : null);
                              }}
                            >
                              <SelectTrigger
                                className="h-6 flex-1 text-[10px]"
                                data-testid={`quick-picker-cell-${slot}-buf-${v.id}-d-${d.day_number}`}
                              >
                                <SelectValue placeholder="—" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={NONE}>— (no theme) —</SelectItem>
                                {(themes || []).map((t) => (
                                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {busy[`b::${d.day_number}::${v.id}::${slot}`] && (
                              <Loader2 className="h-3 w-3 animate-spin text-amber-500" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
